import {
  BRIDGE_OBJECT,
  type ApiRequest,
  type ApiResponse,
  type BridgeEvents,
  type BridgeMethod,
  type AllBridgeMethods,
} from "@trip/contracts/bridge";

/** The object native injects with WebViewCompat.addWebMessageListener (strings only). */
export type NativePort = {
  postMessage(message: string): void;
  onmessage: ((event: { data: unknown }) => void) | null;
};

export class BridgeError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Request/response over the native port: `{id, method, params}` out, `{id, result|error}` back,
 * plus `{event, data}` pushed by native. Tokens never pass through here.
 */
export function createBridge(port: NativePort, opts: { timeoutMs?: number } = {}) {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  const listeners = new Map<string, Set<(data: unknown) => void>>();
  let seq = 0;

  port.onmessage = (e) => {
    let msg: {
      id?: string;
      result?: unknown;
      error?: { code: string; message: string };
      event?: string;
      data?: unknown;
    };
    try {
      msg = JSON.parse(String(e.data));
    } catch {
      return;
    }
    if (typeof msg.event === "string") {
      listeners.get(msg.event)?.forEach((l) => l(msg.data));
      return;
    }
    const p = msg.id ? pending.get(msg.id) : undefined;
    if (!p) return;
    pending.delete(msg.id!);
    if (msg.error) p.reject(new BridgeError(msg.error.code, msg.error.message));
    else p.resolve(msg.result);
  };

  function call<M extends BridgeMethod>(
    method: M,
    params: AllBridgeMethods[M]["params"],
  ): Promise<AllBridgeMethods[M]["result"]> {
    const id = `r${Date.now().toString(36)}_${(++seq).toString(36)}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(new BridgeError("TIMEOUT", "The app did not answer."));
      }, timeoutMs);
      pending.set(id, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v as AllBridgeMethods[M]["result"]);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      });
      port.postMessage(JSON.stringify({ id, method, params }));
    });
  }

  function on<E extends keyof BridgeEvents>(event: E, fn: (data: BridgeEvents[E]) => void) {
    const set = listeners.get(event) ?? new Set();
    set.add(fn as (data: unknown) => void);
    listeners.set(event, set);
    return () => set.delete(fn as (data: unknown) => void);
  }

  const transport = (req: ApiRequest): Promise<ApiResponse> => call("api.request", req);
  return { call, on, transport };
}

export type Bridge = ReturnType<typeof createBridge>;

let installed: Bridge | null = null;

/** The native bridge when running inside the Android shell, otherwise null (a normal browser). */
export function nativeBridge(): Bridge | null {
  if (installed) return installed;
  const port = (globalThis as Record<string, unknown>)[BRIDGE_OBJECT] as NativePort | undefined;
  if (!port || typeof port.postMessage !== "function") return null;
  installed = createBridge(port);
  return installed;
}
